import React, { useCallback, useState } from 'react';
import * as css from './ROHistoryCardPanel.module.scss';
import { strings } from 'src/localization';
import { ROHistory, VehicleRec, ascCtaInteractionInput } from 'src/models';
import { Link, Dialog } from '@framework-controls/core';
import classNames from 'classnames';
import ROHistoryCardPanelDetail from 'src/components/ROHistoryCard/ROHistoryCardPanel/ROHistoryCardPanelDetail/containers/ROHistoryCardPanelDetail';
import { IconRemove, IconRemoveAudi } from 'src/content/icons';
import { THEME } from 'src/constants/ThemeNames';
import { ascCtaInteraction } from 'src/helpers/EventHelper/EventHelper';

export interface IProps {
    id: string;
    opCode: string;
    roHistory: ROHistory[];
    scrollList: boolean;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    theme: string;
}

const ROHistoryCardPanel = (props: IProps) => {
    // determine which history RO we are dealing with
    const histIndex = props.roHistory.findIndex(result => result.RO === props.id);
    const opIndex = histIndex !== -1 &&
        props.roHistory[histIndex]?.detail ?
        props.roHistory[histIndex].detail.findIndex(result => result.opCode === props.opCode): -1;

    const [openLink, setOpenLink] = useState(false);

    const onClick = useCallback((e: any) => {
        setOpenLink(true);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'item',
            elementText: props.id,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'popup',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.id, props.vehicles, props.selectedVehicle]);

    const onClose = useCallback((e: any) => {
        setOpenLink(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'item',
            elementText: 'x',
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    // Audi only: Enter/Space on the close wrapper runs the same close as onClick.
    const closeKeyHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose(e);
        }
    }, [onClose]);

    const backdropHandler = useCallback((e: any) => {
        setOpenLink(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'item',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const gridRowClassName = classNames(
        css.gridRow,
        {
            [css.gridRowScrolled]: props.scrollList,
        },
    );
    // If no valid histIndex or opIndex, return
    if (histIndex === -1 || opIndex === -1) {
        return null;
    }

    const clickHereStyles = classNames(
        css.clickHere,
        {
            [css.clickHereBMW]: props.theme === 'bmw',

        },
    );

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxMazda]: props.theme === 'mazda',
            [css.headerboxGenesis]: props.theme === 'genesis',
            [css.headerboxAudi]: props.theme === THEME.AUDI,
        },
    );

    const contentStyles = classNames(
        css.content,
        {
            [css.contentAudi]: props.theme === THEME.AUDI,
        },
    );

    const paperStyles = classNames(
        css.paper,
        {
            [css.paperAudi]: props.theme === THEME.AUDI,
        },
    );

    return (
        <div>
            <div className={gridRowClassName}>
                <span>{props.roHistory[histIndex].detail[opIndex].opDesc}</span>
                <span>{props.roHistory[histIndex].date}</span>
                <span>
                    <Link
                        className={clickHereStyles}
                        key={props.id + props.opCode}
                        id={'clickHere'}
                        onClick={(e: any) => onClick(e)}>
                        {props.roHistory[histIndex].RO}
                    </Link>
                    <Dialog
                        cssClasses={{
                            content: contentStyles,
                            paper: paperStyles,
                            header: css.LightBoxHeader,
                        }}
                        isOpen={openLink}
                        anchorOrigin={{
                            vertical: 'top',
                        }}
                        header={strings.RepairDetails}
                        hideSave={true}
                        hideCancel={true}
                        disableUpperCase={true}
                        onCancel={() => setOpenLink(false)}
                        onBackDropClick={backdropHandler}
                        width={'full'}
                        saveLabel={strings.SavePrintInvoice}
                    >
                        <div className={headerBoxStyles}>
                            {strings.RepairDetails}
                            {/* Audi: wrapper owns click and keyboard. Other themes: master's markup. */}
                            {props.theme === THEME.AUDI ?
                                <div className={classNames(css.headerIcon, css.headerIconAudi)}
                                    onClick={onClose}
                                    onKeyDown={closeKeyHandler}
                                    role='button'
                                    tabIndex={0}
                                    aria-label={strings.Close}>
                                    <IconRemoveAudi id='closeBtn'/>
                                </div> :
                                <div className={css.headerIcon}>
                                    <IconRemove id='closeBtn' onClick={onClose}/>
                                </div>}
                        </div>
                        <ROHistoryCardPanelDetail
                            histIndex={histIndex}
                            vehicles={props.vehicles}
                            selectedVehicle={props.selectedVehicle}
                        />
                    </Dialog>
                </span>
            </div>
            <hr />
        </div>
    );
};

export default ROHistoryCardPanel;